import React, { useState, useRef, useEffect } from 'react';
import { FileItem } from '../../types/files';
import { getFileIcon } from '../../utils/fileIcons';
import { useTranslation } from 'react-i18next';

interface FileNodeProps {
  item: FileItem;
  level: number;
  isActive: boolean;
  isSelected: boolean;
  isPreview: boolean;
  onFileClick: (file: FileItem, options?: { preview?: boolean }) => void;
  onContextMenu: (e: React.MouseEvent, item: FileItem) => void;
  onDragStart: (path: string) => void;
  onDragOver: (e: React.DragEvent, path: string) => void;
  onDragLeave: (e: React.DragEvent, path: string) => void;
  onDrop: (e: React.DragEvent, item: FileItem) => void;
  onSelect: (path: string) => void;
  renameItem: FileItem | null;
  renameValue: string;
  onRenameChange: (value: string) => void;
  onRenameKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRenameBlur: () => void;
  dropTargetPath: string | null;
}

export const FileNode = React.memo<FileNodeProps>(({
  item,
  level,
  isActive,
  isSelected,
  isPreview,
  onFileClick,
  onContextMenu,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onSelect,
  renameItem,
  renameValue,
  onRenameChange,
  onRenameKeyDown,
  onRenameBlur,
  dropTargetPath
}) => {
  const { t } = useTranslation('FileExplorer');
  const inputRef = useRef<HTMLInputElement>(null);
  const isDropTarget = dropTargetPath === item.path;

  useEffect(() => {
    if (renameItem && renameItem.path === item.path && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renameItem, item.path]);

  const handleClick = () => {
    onFileClick(item, { preview: true });
    onSelect(item.path);
  };

  const handleDoubleClick = () => {
    if (isPreview) {
      onFileClick(item, { preview: false });
    } else {
      onFileClick(item, { preview: true });
    }
    onSelect(item.path);
  };

  return (
    <div
      className={`file-tree-item file${isActive ? ' active' : ''}${isSelected ? ' selected' : ''}${item.status ? ` ${item.status}` : ''}${isDropTarget ? ' drop-target' : ''}`}
      style={{ paddingLeft: `${level * 15}px` }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={(e) => onContextMenu(e, item)}
      tabIndex={0}
      draggable
      onDragStart={() => onDragStart(item.path)}
      onDragOver={(e) => onDragOver(e, item.path)}
      onDragLeave={(e) => onDragLeave(e, item.path)}
      onDrop={(e) => onDrop(e, item)}
    >
      <span className="file-icon">
        <img src={getFileIcon(item.name, false)} alt={item.name} className="icon" />
      </span>
      {renameItem && renameItem.path === item.path ? (
        <input
          ref={inputRef}
          value={renameValue}
          onChange={(e) => onRenameChange(e.target.value)}
          onKeyDown={onRenameKeyDown}
          className="new-item-input rename-input"
          autoFocus
          onBlur={onRenameBlur}
          style={{ width: 140 }}
        />
      ) : (
        <span>{item.name}</span>
      )}
    </div>
  );
});